import { PhoneOutline24Icon } from "@vygruppen/spor-icon-react";
import { useGetTrainStations } from "features/VehicleSidebar/VehicleList/AddVehicles/useInfoDataForVehicles";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { CallButton } from "shared/components/buttons/CallButton";
import { TimeInterval } from "shared/components/dataDisplay/TimeInterval";
import { Badge, Box, Button, Flex, Text } from "shared/components/Spor";
import type { StopReference } from "shared/types/alternativeTransport";
import { VehicleType } from "shared/types/alternativeTransport";
import { toSentenceCase } from "shared/utils/alternativeTransport";

// Finds the train station corresponding to the given base stop reference.
const useBaseStopStation = (baseStopReference?: StopReference | null) => {
  const { data } = useGetTrainStations(
    Boolean(baseStopReference?.jbvId || baseStopReference?.nsrCode),
  );

  return useMemo(
    () =>
      data?.railStations.find((trainStation) =>
        trainStation.externalIds.some(
          (externalId) =>
            (externalId.sourceSystem === "TRAINPLAN" &&
              externalId.id === baseStopReference?.jbvId) ||
            (externalId.sourceSystem === "NSR" &&
              externalId.id === baseStopReference?.nsrCode),
        ),
      ),
    [data?.railStations, baseStopReference?.jbvId, baseStopReference?.nsrCode],
  );
};

const getVehicleText = (vehicleType: VehicleType) => {
  const { t } = useTranslation();
  switch (vehicleType) {
    case "BUS":
      return t("Buss");
    case "MINIBUS":
      return t("Minibuss");
    case "TAXI":
      return t("Taxi");
    case "MAXITAXI":
      return t("MaxiTaxi");
    case "UNKNOWN":
      return t("Ukjent kjøretøy");
  }
};

const Component = ({
  title,
  size = "md",
  child,
}: {
  title: string;
  size?: "sm" | "md" | "lg";

  child: string | React.ReactNode;
}) => {
  let minWidth = "90px";

  if (size === "md") {
    minWidth = "120px";
  } else if (size === "lg") {
    minWidth = "240px";
  }

  return (
    <Flex direction="column" gap="0.5" minWidth={minWidth}>
      <Text
        fontWeight="700"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        overflow="hidden"
      >
        {title}
      </Text>
      {typeof child === "string" ? <Text>{child}</Text> : child}
    </Flex>
  );
};

export const VehicleSummary = ({
  type,
  stations,
  trains,
  tripDetails,
  driverPhoneNumber,
  lineNames,
  operatorName,
  startTime,
  endTime,
  availableFrom,
  availableTo,
  location,
  reference,
  baseStopReference,
}: {
  type: VehicleType;
  tripDetails?: string | null;
  driverPhoneNumber: string | null;
  operatorName: string | null;
  startTime?: Date | null; // Operational bus
  endTime?: Date | null; // Operational bus
  stations?: string | null; // Operational bus
  trains?: string | null; // Operational bus
  lineNames?: string | null; // Operational bus
  availableFrom?: Date | null; // Reserve | Back-up bus
  availableTo?: Date | null; // Reserve | Back-up bus
  location?: string | null; // Reserve | Back-up bus
  reference?: string | null; // Reserve | Back-up bus
  baseStopReference?: StopReference | null; // Reserve | Back-up bus
}) => {
  const typeText = getVehicleText(type);
  const station = useBaseStopStation(baseStopReference);

  const phoneNumberWithDigitsOnly = driverPhoneNumber
    ? driverPhoneNumber.replace("+", "00")
    : null;

  const overflowCropFixPadding = "1px";

  return (
    <Flex gap="2" overflowX="scroll">
      {trains && lineNames && tripDetails && (
        <Component title={`${trains} ${lineNames}`} child={tripDetails} />
      )}
      {stations && <Component title="Strekning" child={stations} size="lg" />}

      <Component
        title={operatorName ?? "[Leverandør]"}
        child={
          <Box pl={overflowCropFixPadding} pb={overflowCropFixPadding}>
            {phoneNumberWithDigitsOnly ? (
              <CallButton phoneNumber={phoneNumberWithDigitsOnly} size="xs" />
            ) : (
              <Button
                leftIcon={<PhoneOutline24Icon />}
                variant="tertiary"
                disabled={!driverPhoneNumber}
                size="xs"
              >
                Ikke registrert
              </Button>
            )}
          </Box>
        }
        size="lg"
      />
      <Component title="Kjøretøy" size="sm" child={typeText} />

      {startTime && endTime && (
        <Component
          title="Tidsperiode"
          child={<TimeInterval startTime={startTime} endTime={endTime} />}
          size="lg"
        />
      )}
      {availableFrom && availableTo && (
        <Component
          title="Tilgjengelig"
          child={
            <TimeInterval startTime={availableFrom} endTime={availableTo} />
          }
          size="lg"
        />
      )}
      {location && <Component title="Lokasjon" child={location} size="lg" />}
      {station && <Component title="Stasjon" child={station.name} size="lg" />}
      {reference && <Component title="Referanse" child={reference} size="sm" />}
    </Flex>
  );
};

export const VehicleTitle = ({
  externalSystem,
  isAvailable,
  workShiftId,
  type,
}: {
  externalSystem?: string | null;
  isAvailable: boolean;
  workShiftId: string | null;
  type: "BACKUP" | "RESERVE";
}) => {
  const location = useLocation();
  return (
    <Flex gap="1" alignItems="center">
      <Text variant="ml" fontWeight="700">
        {location.state?.vehicleTitle ??
          `${type === "BACKUP" ? "Backup" : "Reserve"} ${workShiftId}`}
      </Text>
      {externalSystem && (
        <>
          <Text variant="ml" fontWeight="400">
            |
          </Text>

          <Text variant="ml" fontWeight="400">
            {toSentenceCase(externalSystem)}
          </Text>
        </>
      )}

      {!isAvailable && (
        <Badge ml="0.5" colorPalette="blue">
          I bruk
        </Badge>
      )}
    </Flex>
  );
};
